import React from 'react';
import PropTypes from 'prop-types';
import { useMutation } from '@apollo/client';
import { ValidatorForm } from 'react-form-validator-core';
import styled from 'styled-components';

import useAlerts from 'core/hooks/useAlerts';
import useForm from 'core/hooks/useForm';
import useUser from 'core/hooks/useUser';

import { formatPhoneNumber } from 'core/api/utils';
import { PHONE_REGEXP, ERROR_MESSAGES } from 'core/api/constants';
import { SET_ACCOUNT_INFO } from 'core/api/apollo/mutations';

import { Box, Flex, Hr, FormGroup, Label, ValidatedInput } from 'core/ui/atoms';
import { SubmitButton } from 'settings/ui/atoms';
import ModalV2 from 'core/ui/components/V2/ModalV2';
import { NavigationHeader } from 'onramp/ui/components';

const ContactModal = ({ closeModal }) => {
  const { currentUser } = useUser();
  const alerts = useAlerts();

  const { firstName, lastName, email, phoneNumber } = currentUser;

  const onSuccess = () => {
    closeModal();
    alerts.show('Your changes have been saved!', { autoClose: true });
  };

  return (
    <ModalV2 title='Account Info' data-account-info-modal onClose={closeModal}>
      <Container>
        <NavigationHeader backNavigate={closeModal} title="Personal Details" />
        <Hr />
        <AccountInfoForm
          initialData={{ firstName, lastName, email, phoneNumber }}
          onSuccess={onSuccess}
        />
      </Container>
    </ModalV2>
  );
};

const AccountInfoForm = (props) => {
  const { initialData, onSuccess } = props;
  const [setAccountInfo] = useMutation(SET_ACCOUNT_INFO);
  const { handleSubmit, formData, onChange, isDirty, isProcessing } = useForm('setAccountInfo', {
    initialData,
    onSubmit: setAccountInfo,
    onSuccess,
    validator: ({ firstName, lastName, email, phoneNumber }) => (firstName && lastName && email && phoneNumber),
  });

  return (
    <ValidatorForm
      onSubmit={handleSubmit}
      noValidate
      instantValidate={false}
    >
      <FormGroup>
        <CustomLabel required>First Name</CustomLabel>
        <ValidatedInput
          type="text"
          name="firstName"
          placeholder="Type first name"
          value={formData.firstName}
          onChange={onChange}
          validators={['required']}
          errorMessages={[ERROR_MESSAGES.required]}
        />
      </FormGroup>

      <FormGroup>
        <CustomLabel required>Last Name</CustomLabel>
        <ValidatedInput
          type="text"
          name="lastName"
          placeholder="Type last name"
          value={formData.lastName}
          onChange={onChange}
          validators={['required']}
          errorMessages={[ERROR_MESSAGES.required]}
        />
      </FormGroup>

      <FormGroup>
        <CustomLabel required>Phone Number</CustomLabel>
        <ValidatedInput
          name="phoneNumber"
          onChange={onChange}
          value={formatPhoneNumber(formData.phoneNumber) || formData.phoneNumber}
          type="tel"
          placeholder="ex. (123) 456-7890"
          validators={['required', `matchRegexp:${PHONE_REGEXP}`]}
          errorMessages={[ERROR_MESSAGES.required, ERROR_MESSAGES.phone]}
        />
      </FormGroup>

      <FormGroup>
        <CustomLabel required>Email</CustomLabel>
        <ValidatedInput
          type="email"
          placeholder="Type email address"
          name="email"
          disabled={true}
          value={formData.email}
          onChange={onChange}
          validators={['required', 'isEmail']}
          errorMessages={[ERROR_MESSAGES.required, ERROR_MESSAGES.email]}
        />
      </FormGroup>

      <FooterButton direction='row' justifyContent='center'>
        <SaveButton disabled={!isDirty || isProcessing}>
          SAVE
        </SaveButton>
      </FooterButton>
    </ValidatorForm>
  );
};


const CustomLabel = styled(Label)`
  padding: 0px;
  padding-bottom: 10px;
`;

const FooterButton = styled(Flex)`
  margin-top: calc(10vh);
`;

const Container = styled(Box)`
  padding-top: 50px;
`;

const SaveButton = styled(SubmitButton)`
  border-radius: 4px;
  border-color: #C5D1C0;
  background: #C5D1C0;
  height: 52px;
  width: 90%;
  color: #36443A;
  text-align: center;
  font-family: ${({ theme }) => theme.typography.familyPoppins};
  font-size: 15px;
  font-style: normal;
  font-weight: 600;
  line-height: 28px;
  letter-spacing: 1.5px;
`;

ContactModal.propTypes = {
  closeModal: PropTypes.func,
};

AccountInfoForm.propTypes = {
  initialData: PropTypes.object.isRequired,
  onSuccess: PropTypes.func,
};

export default ContactModal;
